/* SawaKart chrome (P4): <sk-announcement>, <sk-mega-nav>, <sk-mobile-drawer>, <sk-rewards>.
   Custom elements self-initialise (theme editor safe) and clean up in disconnectedCallback.
   Timings mirror the FE storefront (framer-motion values, see S05 §14). */
(function () {
  'use strict';

  var EASE_INOUT = 'cubic-bezier(.42,0,.58,1)'; /* framer default tween */
  var EASE_OUT = 'cubic-bezier(0,0,.58,1)'; /* framer "easeOut" */
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    if (window.SK && typeof window.SK.reducedMotion === 'function') return !!window.SK.reducedMotion();
    return !!((mqReduce && mqReduce.matches) || (document.body && 'reduceAnimations' in document.body.dataset));
  }
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }
  function animateEl(el, frames, opts) {
    if (!el || typeof el.animate !== 'function') return Promise.resolve();
    var a = el.animate(frames, opts);
    return a.finished.then(function () { return a; }, function () { return null; });
  }

  /* ------------------------------------------------------------------
     <sk-announcement>: rotate messages; exit (350ms) then enter (350ms)
     ------------------------------------------------------------------ */
  class SkAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;
      this.msgs = Array.prototype.slice.call(this.querySelectorAll('[data-sk-ann-msg]'));
      this.index = 0;
      for (var i = 0; i < this.msgs.length; i++) if (this.msgs[i].classList.contains('is-active')) this.index = i;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      this.paused = false;
      var self = this;
      var first = this.msgs[this.index];
      if (first) void window.getComputedStyle(first).opacity; /* flush the hidden start state */
      this._raf = requestAnimationFrame(function () { self.setAttribute('data-ready', ''); });
      this.start();
    }
    disconnectedCallback() {
      this._on = false;
      this.stop();
      clearTimeout(this._swapT);
      cancelAnimationFrame(this._raf);
    }
    start() {
      var self = this;
      this.stop();
      if (this.paused || this.msgs.length < 2) return;
      this._timer = setInterval(function () { self.show((self.index + 1) % self.msgs.length); }, this.interval);
    }
    stop() {
      clearInterval(this._timer);
      this._timer = null;
    }
    pause() { this.paused = true; this.stop(); }
    resume() { this.paused = false; this.start(); }
    _setVisible(el, on) {
      if (on) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', 'true');
      var link = el.querySelector('a');
      if (link) { if (on) link.removeAttribute('tabindex'); else link.setAttribute('tabindex', '-1'); }
    }
    show(i, instant) {
      var cur = this.msgs[this.index];
      var nxt = this.msgs[i];
      if (!nxt) return;
      clearTimeout(this._swapT);
      var self = this;
      this.msgs.forEach(function (m) { if (m !== cur) m.classList.remove('is-leaving'); });
      if (instant) {
        this.msgs.forEach(function (m) { m.classList.remove('is-active', 'is-leaving'); self._setVisible(m, false); });
        nxt.classList.add('is-active');
        this._setVisible(nxt, true);
        this.index = i;
        return;
      }
      if (i === this.index) return;
      if (cur) {
        cur.classList.remove('is-active');
        cur.classList.add('is-leaving');
        this._setVisible(cur, false);
      }
      this.index = i;
      /* AnimatePresence mode="wait": the enter starts after the exit finished */
      this._swapT = setTimeout(function () {
        if (cur) cur.classList.remove('is-leaving');
        nxt.classList.add('is-active');
        self._setVisible(nxt, true);
      }, 350);
    }
  }
  define('sk-announcement', SkAnnouncement);

  /* ------------------------------------------------------------------
     <sk-mega-nav>: hover opens instantly (first level-2 pre-selected),
     140ms close delay after leaving row + panel, keyboard + touch support
     ------------------------------------------------------------------ */
  class SkMegaNav extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;
      var self = this;
      this.delay = parseInt(this.dataset.closeDelay, 10);
      if (isNaN(this.delay)) this.delay = 140;
      this.openKey = null;
      this.panels = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-sk-mega-panel]'), function (p) {
        self.panels[p.getAttribute('data-sk-mega-panel')] = p;
      });
      this.roots = Array.prototype.slice.call(this.querySelectorAll('[data-sk-mega-root]'));

      if (!this._bound) {
        this._bound = true;
        this.addEventListener('mouseenter', function () { self.cancelClose(); });
        this.addEventListener('mouseleave', function () { self.scheduleClose(); });
        this.roots.forEach(function (r) {
          r.addEventListener('mouseenter', function () { self.enterRoot(r); });
        });
        Array.prototype.forEach.call(this.querySelectorAll('[data-sk-mega-l2]'), function (l) {
          l.addEventListener('mouseenter', function () { self.select(l); });
        });
        this.addEventListener('focusin', function (e) {
          self.cancelClose();
          var r = e.target.closest('[data-sk-mega-root]');
          if (r) { if (self.openKey !== r.getAttribute('data-sk-mega-root')) self.enterRoot(r); return; }
          var l = e.target.closest('[data-sk-mega-l2]');
          if (l) self.select(l);
        });
        this.addEventListener('focusout', function (e) {
          if (!e.relatedTarget || !self.contains(e.relatedTarget)) self.scheduleClose();
        });
        this.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.openKey !== null) {
            var root = self.rootEl(self.openKey);
            self.close();
            if (root) root.focus();
            return;
          }
          var down = e.key === 'ArrowDown' && e.target.closest('[data-sk-mega-root]');
          if (down && !down.hasAttribute('data-sk-mega-leaf')) {
            /* keyboard entry into the open panel (addition; FE is hover-only) */
            e.preventDefault();
            self.enterRoot(down);
            var pnl = self.panels[down.getAttribute('data-sk-mega-root')];
            var firstL2 = pnl && pnl.querySelector('[data-sk-mega-l2]');
            if (firstL2) firstL2.focus();
            return;
          }
          var r = e.target.closest('span[data-sk-mega-root]');
          if (r && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (self.openKey === r.getAttribute('data-sk-mega-root')) self.close(); else self.enterRoot(r);
          }
        });
        this.addEventListener('pointerdown', function (e) {
          var r = e.target.closest('[data-sk-mega-root]');
          self._ptype = e.pointerType;
          self._wasOpen = !!(r && self.openKey === r.getAttribute('data-sk-mega-root'));
        }, true);
        this.addEventListener('click', function (e) {
          var r = e.target.closest('[data-sk-mega-root]');
          if (!r || r.hasAttribute('data-sk-mega-leaf')) return;
          if (r.tagName !== 'A') {
            /* non-navigating "#" root: click toggles */
            if (self._wasOpen && self._ptype !== 'mouse') self.close(); else self.enterRoot(r);
            return;
          }
          /* touch: first tap opens the panel, second tap follows the link */
          if ((self._ptype === 'touch' || self._ptype === 'pen') && !self._wasOpen) {
            e.preventDefault();
            self.enterRoot(r);
          }
        });
      }
      this._onDocDown = function (e) { if (self.openKey !== null && !self.contains(e.target)) self.close(); };
      this._onResize = function () { if (window.innerWidth < 768 && self.openKey !== null) self.close(); };
      document.addEventListener('pointerdown', this._onDocDown);
      window.addEventListener('resize', this._onResize);
    }
    disconnectedCallback() {
      this._on = false;
      clearTimeout(this._t);
      document.removeEventListener('pointerdown', this._onDocDown);
      window.removeEventListener('resize', this._onResize);
    }
    rootEl(key) {
      for (var i = 0; i < this.roots.length; i++) if (this.roots[i].getAttribute('data-sk-mega-root') === key) return this.roots[i];
      return null;
    }
    cancelClose() { clearTimeout(this._t); this._t = null; }
    scheduleClose() {
      var self = this;
      this.cancelClose();
      if (this.openKey === null) return;
      this._t = setTimeout(function () { self.close(); }, this.delay);
    }
    enterRoot(r) {
      this.cancelClose();
      var key = r.getAttribute('data-sk-mega-root');
      if (r.hasAttribute('data-sk-mega-leaf') || !this.panels[key]) { this.close(); return; }
      this.open(key);
    }
    open(key) {
      var panel = this.panels[key];
      if (!panel) return;
      if (this.openKey !== key) {
        if (this.openKey !== null) this._setState(this.openKey, false);
        this.openKey = key;
        this._setState(key, true);
      }
      var first = panel.querySelector('[data-sk-mega-l2]');
      if (first) this.select(first);
    }
    close() {
      this.cancelClose();
      if (this.openKey === null) return;
      this._setState(this.openKey, false);
      this.openKey = null;
    }
    _setState(key, on) {
      var panel = this.panels[key];
      var root = this.rootEl(key);
      if (panel) panel.classList.toggle('is-open', on);
      if (root) {
        root.classList.toggle('is-open', on);
        root.setAttribute('aria-expanded', on ? 'true' : 'false');
      }
    }
    select(l2) {
      var panel = l2.closest('[data-sk-mega-panel]');
      if (!panel) return;
      var idx = l2.getAttribute('data-sk-mega-l2');
      Array.prototype.forEach.call(panel.querySelectorAll('[data-sk-mega-l2]'), function (el) {
        el.classList.toggle('is-selected', el.getAttribute('data-sk-mega-l2') === idx);
      });
      Array.prototype.forEach.call(panel.querySelectorAll('[data-sk-mega-pane]'), function (el) {
        el.hidden = el.getAttribute('data-sk-mega-pane') !== idx;
      });
    }
  }
  define('sk-mega-nav', SkMegaNav);

  /* ------------------------------------------------------------------
     <sk-mobile-drawer>: spring panel, stack levels (220ms out, then 220ms in)
     ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SkMobileDrawer extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;
      var self = this;
      this.panel = this.querySelector('.sk-drawer__panel');
      this.titleEl = this.querySelector('[data-sk-drawer-title]');
      this.backBtn = this.querySelector('[data-sk-drawer-back]');
      this.foot = this.querySelector('[data-sk-drawer-foot]');
      this.rootLevel = this.querySelector('[data-sk-level="root"]');
      this.stack = [this.rootLevel];
      this.isOpen = false;
      if (!this._bound) {
        this._bound = true;
        this.addEventListener('click', function (e) {
          var push = e.target.closest('[data-sk-drawer-push]');
          if (push) {
            e.preventDefault();
            self._build();
            self.push(document.getElementById(push.getAttribute('data-sk-drawer-push')), e.detail === 0);
            return;
          }
          if (e.target.closest('[data-sk-drawer-back]')) { self.pop(e.detail === 0); return; }
          if (e.target.closest('[data-sk-drawer-close]')) { self.close(); return; }
          if (e.target.closest('[data-sk-drawer-link]')) self.close(true); /* navigation continues */
        });
      }
      /* bound on document while open, so Escape/Tab work even if focus fell back to <body> */
      this._onDocKey = function (e) {
        if (!self.isOpen) return;
        if (e.key === 'Escape') { e.stopPropagation(); self.close(); } else if (e.key === 'Tab') self._trap(e);
      };
      this._mq = window.matchMedia('(min-width: 768px)');
      this._onMq = function () { if (self._mq.matches && self.isOpen) self.close(true); };
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
    }
    disconnectedCallback() {
      this._on = false;
      clearTimeout(this._hideT);
      if (this._mq && this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
      document.removeEventListener('keydown', this._onDocKey, true);
      if (this.isOpen) document.documentElement.classList.remove('sk-scroll-lock');
    }
    open(trigger) {
      if (this.isOpen) return;
      clearTimeout(this._hideT);
      this.isOpen = true;
      this.trigger = trigger || null;
      this._build();
      this._reset();
      this.hidden = false;
      document.documentElement.classList.add('sk-scroll-lock');
      void this.panel.offsetWidth; /* start from the closed transform */
      this.classList.add('is-open');
      document.addEventListener('keydown', this._onDocKey, true);
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      try { this.panel.focus({ preventScroll: true }); } catch (err) { this.panel.focus(); }
    }
    close(skipFocus) {
      if (!this.isOpen) return;
      var self = this;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onDocKey, true);
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (!skipFocus) { try { this.trigger.focus({ preventScroll: true }); } catch (err) { this.trigger.focus(); } }
      }
      /* unmount after the spring (~400ms) and overlay (200ms) finished */
      this._hideT = setTimeout(function () {
        if (self.isOpen) return;
        self.hidden = true;
        self._reset();
        document.documentElement.classList.remove('sk-scroll-lock');
      }, reduced() ? 220 : 420);
    }
    /* SK-63: the levels below the first one are built from the desktop mega menu on the same page the first
       time the drawer opens (sk-mobile-drawer.liquid no longer prints them): a department's level lists its
       sub-menus with "Shop all"; a sub-menu with links gets its own level. Same ids, rows and links as the
       server-rendered levels had. */
    _build() {
      if (this._built) return;
      this._built = true;
      var body = this.querySelector('.sk-drawer__body');
      var mega = document.querySelector('sk-mega-nav');
      var chev = this.rootLevel && this.rootLevel.querySelector('.sk-drawer__chev');
      if (!body || !mega || !this.rootLevel) return;
      function el(tag, cls, attrs) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (attrs) Object.keys(attrs).forEach(function (k) { n.setAttribute(k, attrs[k]); });
        return n;
      }
      function label(text) {
        var s = el('span', 'sk-drawer__label');
        s.textContent = text;
        return s;
      }
      function level(id, title) {
        return el('nav', 'sk-drawer__level', { id: id, 'data-sk-level': '', 'data-sk-title': title, 'aria-label': title, hidden: '' });
      }
      function allLink(href, title) {
        var a = el('a', 'sk-drawer__all', { href: href, 'data-sk-drawer-link': '' });
        a.textContent = 'Shop all ' + title + ' →';
        return a;
      }
      function linkRow(href, text) {
        var a = el('a', 'sk-drawer__row', { href: href, 'data-sk-drawer-link': '' });
        a.appendChild(label(text));
        return a;
      }
      function textRow(text) {
        var s = el('span', 'sk-drawer__row');
        s.appendChild(label(text));
        return s;
      }
      function pushRow(id, text) {
        var b = el('button', 'sk-drawer__row', { type: 'button', 'data-sk-drawer-push': id });
        b.appendChild(label(text));
        if (chev) b.appendChild(chev.cloneNode(true));
        return b;
      }
      var frag = document.createDocumentFragment();
      Array.prototype.forEach.call(this.rootLevel.querySelectorAll('[data-sk-drawer-push][data-sk-root]'), function (btn) {
        var id = btn.getAttribute('data-sk-drawer-push');
        if (document.getElementById(id)) return; /* already in the page (an older cached page) */
        var n = btn.getAttribute('data-sk-root');
        var panel = mega.querySelector('[data-sk-mega-panel="' + n + '"]');
        if (!panel) return;
        var rootA = mega.querySelector('[data-sk-mega-root="' + n + '"]');
        var title = (btn.querySelector('.sk-drawer__label') || btn).textContent.trim();
        var nav = level(id, title);
        if (rootA && rootA.getAttribute('href')) nav.appendChild(allLink(rootA.getAttribute('href'), title));
        var ul = el('ul', '', { role: 'list' });
        var subs = [];
        Array.prototype.forEach.call(panel.querySelectorAll('.sk-mega__list > li > .sk-mega__l2'), function (l2) {
          var k = l2.getAttribute('data-sk-mega-l2');
          var text = (l2.querySelector('.sk-mega__l2-label') || l2).textContent.trim();
          var pane = panel.querySelector('[data-sk-mega-pane="' + k + '"]');
          var l3s = pane ? pane.querySelectorAll('.sk-mega__links .sk-mega__l3') : [];
          var href = l2.tagName === 'A' ? l2.getAttribute('href') : null;
          var li = el('li');
          if (l3s.length) {
            var subId = id + '-' + k;
            li.appendChild(pushRow(subId, text));
            var sub = level(subId, text);
            if (href) sub.appendChild(allLink(href, text));
            var ul3 = el('ul', '', { role: 'list' });
            Array.prototype.forEach.call(l3s, function (l3) {
              var li3 = el('li');
              /* SK-70: the name only (the menu link also carries its department for screen readers) */
              var l3text = (l3.querySelector('.sk-mega__l3-label') || l3).textContent.trim();
              li3.appendChild(l3.tagName === 'A' ? linkRow(l3.getAttribute('href'), l3text) : textRow(l3text));
              ul3.appendChild(li3);
            });
            sub.appendChild(ul3);
            subs.push(sub);
          } else if (href) {
            li.appendChild(linkRow(href, text));
          } else {
            li.appendChild(textRow(text));
          }
          ul.appendChild(li);
        });
        nav.appendChild(ul);
        frag.appendChild(nav);
        subs.forEach(function (sub) { frag.appendChild(sub); });
      });
      body.appendChild(frag);
    }

    _reset() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-sk-level]'), function (lv) {
        if (lv.getAnimations) lv.getAnimations().forEach(function (a) { a.cancel(); });
        var isRoot = lv === self.rootLevel;
        lv.hidden = !isRoot;
        lv.classList.toggle('is-current', isRoot);
        lv.scrollTop = 0;
      });
      this.stack = [this.rootLevel];
      this.animating = false;
      this._head();
    }
    _head() {
      var top = this.stack[this.stack.length - 1];
      var isRoot = this.stack.length === 1;
      if (this.titleEl) {
        this.titleEl.textContent = top.getAttribute('data-sk-title') || '';
        this.titleEl.classList.toggle('is-root', isRoot);
      }
      if (this.backBtn) this.backBtn.hidden = isRoot;
      if (this.foot) this.foot.hidden = !isRoot;
    }
    push(next, viaKeyboard) {
      if (!next || this.animating) return;
      var cur = this.stack[this.stack.length - 1];
      this.stack.push(next);
      this._head();
      this._swap(cur, next, 1).then(function () {
        if (viaKeyboard) { var f = next.querySelector(FOCUSABLE); if (f) f.focus(); }
      });
    }
    pop(viaKeyboard) {
      if (this.stack.length < 2 || this.animating) return;
      var self = this;
      var cur = this.stack.pop();
      var prev = this.stack[this.stack.length - 1];
      this._head();
      this._swap(cur, prev, -1).then(function () {
        if (viaKeyboard && self.backBtn) (self.backBtn.hidden ? self.panel : self.backBtn).focus();
      });
    }
    _swap(cur, next, dir) {
      var self = this;
      var r = reduced();
      var opts = { duration: 220, easing: EASE_OUT };
      var exitKf = r
        ? [{ opacity: 1 }, { opacity: 0 }]
        : [{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(' + (-dir * 100) + '%)', opacity: 0 }];
      var enterKf = r
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [{ transform: 'translateX(' + (dir * 100) + '%)', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }];
      this.animating = true;
      return animateEl(cur, exitKf, Object.assign({ fill: 'forwards' }, opts)).then(function (a) {
        cur.hidden = true;
        cur.classList.remove('is-current');
        if (a) a.cancel();
        var active = document.activeElement;
        if (!active || active === document.body || (cur.contains(active))) {
          try { self.panel.focus({ preventScroll: true }); } catch (err) { self.panel.focus(); }
        }
        next.hidden = false;
        next.classList.add('is-current');
        next.scrollTop = 0;
        return animateEl(next, enterKf, opts);
      }).then(function () { self.animating = false; });
    }
    _trap(e) {
      var list = Array.prototype.filter.call(this.panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!list.length) { e.preventDefault(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (e.shiftKey && (active === first || active === this.panel || !this.panel.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !this.panel.contains(active))) {
        e.preventDefault(); first.focus();
      }
    }
  }
  define('sk-mobile-drawer', SkMobileDrawer);

  /* ------------------------------------------------------------------
     <sk-rewards>: pill toggles the loyalty panel (200ms), icon swap 150+150ms
     ------------------------------------------------------------------ */
  class SkRewards extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;
      var self = this;
      this.btn = this.querySelector('.sk-fw__trigger');
      this.panel = this.querySelector('.sk-fw__panel');
      this.gift = this.querySelector('.sk-fw__icon--gift');
      this.x = this.querySelector('.sk-fw__icon--x');
      this.isOpen = false;
      if (!this.btn || !this.panel) return;
      if (!this._bound) {
        this._bound = true;
        this.btn.addEventListener('click', function () { self.setOpen(!self.isOpen); });
      }
      this._onDoc = function (e) {
        if (self.isOpen && !self.panel.contains(e.target) && !self.btn.contains(e.target)) self.setOpen(false);
      };
      this._onKey = function (e) {
        if (e.key === 'Escape' && self.isOpen) { self.setOpen(false); self.btn.focus(); }
      };
      document.addEventListener('mousedown', this._onDoc);
      document.addEventListener('keydown', this._onKey);
    }
    disconnectedCallback() {
      this._on = false;
      clearTimeout(this._hideT);
      document.removeEventListener('mousedown', this._onDoc);
      document.removeEventListener('keydown', this._onKey);
    }
    setOpen(open) {
      if (open === this.isOpen) return;
      var self = this;
      this.isOpen = open;
      this.btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      this._swapIcon(open);
      clearTimeout(this._hideT);
      if (open) {
        this.panel.hidden = false;
        void this.panel.offsetWidth;
        this.panel.classList.add('is-open');
      } else {
        this.panel.classList.remove('is-open');
        this._hideT = setTimeout(function () { if (!self.isOpen) self.panel.hidden = true; }, 200);
      }
    }
    _swapIcon(open) {
      if (!this.gift || !this.x) return;
      var out = open ? this.gift : this.x;
      var inn = open ? this.x : this.gift;
      var deg = open ? -90 : 90; /* gift exits to -90 / X enters from -90; X exits to +90 / gift enters from +90 */
      var r = reduced();
      var token = {};
      this._token = token;
      [this.gift, this.x].forEach(function (el) { if (el.getAnimations) el.getAnimations().forEach(function (a) { a.cancel(); }); });
      out.hidden = false;
      inn.hidden = true;
      function kf(o1, d1, o2, d2) {
        return r ? [{ opacity: o1 }, { opacity: o2 }]
          : [{ opacity: o1, transform: 'rotate(' + d1 + 'deg)' }, { opacity: o2, transform: 'rotate(' + d2 + 'deg)' }];
      }
      var self = this;
      animateEl(out, kf(1, 0, 0, deg), { duration: 150, easing: EASE_INOUT, fill: 'forwards' }).then(function (a) {
        if (self._token !== token) return;
        out.hidden = true;
        if (a) a.cancel();
        inn.hidden = false;
        animateEl(inn, kf(0, deg, 1, 0), { duration: 150, easing: EASE_INOUT });
      });
    }
  }
  define('sk-rewards', SkRewards);

  /* ------------------------------------------------------------------
     Delegated / global listeners (bound once)
     ------------------------------------------------------------------ */
  if (window.__skChromeBound) return;
  window.__skChromeBound = true;

  /* hamburger → drawer */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-sk-drawer-open]');
    if (!t) return;
    e.preventDefault();
    var id = t.getAttribute('aria-controls');
    var d = (id && document.getElementById(id)) || document.querySelector('sk-mobile-drawer');
    if (d && typeof d.open === 'function') d.open(t);
  });

  /* header cart badge: FE shows "99+" above 99 (Empire's own listener sets the number) */
  window.addEventListener('cartcount:update', function (e) {
    var n = e && e.detail ? Number(e.detail.item_count) : 0;
    Array.prototype.forEach.call(document.querySelectorAll('[data-header-cart-count]'), function (el) {
      el.classList.toggle('sk-hdr__count--max', n > 99);
    });
  });

  /* theme editor: selected announcement message stays visible */
  document.addEventListener('shopify:block:select', function (e) {
    var ann = e.target.closest && e.target.closest('sk-announcement');
    if (!ann || !ann.msgs) return;
    var msg = e.target.closest('[data-sk-ann-msg]');
    ann.pause();
    if (msg) ann.show(ann.msgs.indexOf(msg), true);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var ann = e.target.closest && e.target.closest('sk-announcement');
    if (ann && ann.resume) ann.resume();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var ann = e.target.querySelector && e.target.querySelector('sk-announcement');
    if (ann && ann.pause) ann.pause();
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var ann = e.target.querySelector && e.target.querySelector('sk-announcement');
    if (ann && ann.resume) ann.resume();
  });

  /* Search lists products only, like staging: the search forms send type=product, so the results
     page (sections/sk-search) opens once instead of replacing itself with that URL. Empire's live
     search submits a copy of its form, hidden field included. */
  /* ---------- Phone search sheet (SK-56) ----------
     The header's magnifier ([data-mobile-search-button], under 640px) is meant to open Empire's
     "takeover" search, which never shows in this header: the tap did nothing. It now opens our own
     sheet at the top of the screen: the search pill, Go, Cancel; the search goes to the products
     search page (type=product) like the desktop pill. */
  var SEARCH_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>';
  var searchSheet = null;
  function ensureSearchSheet() {
    if (searchSheet) return searchSheet;
    var routes = (window.skTheme && window.skTheme.routes) || (window.Theme && window.Theme.routes) || {};
    var d = document.createElement('dialog');
    d.className = 'sk-ssheet';
    d.setAttribute('aria-label', 'Search');
    d.innerHTML =
      '<form class="sk-ssheet__form" action="' + (routes.search_url || '/search') + '" method="get" role="search">' +
        '<input type="hidden" name="type" value="product">' +
        '<label class="sk-ssheet__field">' + SEARCH_ICON +
          '<input class="sk-ssheet__input" type="search" name="q" placeholder="What are you looking for?" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="search" aria-label="Search">' +
        '</label>' +
        '<button type="submit" class="sk-ssheet__go" aria-label="Search">' + SEARCH_ICON + '</button>' +
        '<button type="button" class="sk-ssheet__cancel" data-sk-ssheet-close>Cancel</button>' +
      '</form>';
    document.body.appendChild(d);
    d.addEventListener('click', function (e) {
      if (e.target === d || (e.target.closest && e.target.closest('[data-sk-ssheet-close]'))) d.close();
    });
    d.addEventListener('submit', function (e) {
      var q = d.querySelector('input[name="q"]');
      if (!q.value.trim()) { e.preventDefault(); q.focus(); }
    });
    searchSheet = d;
    typePlaceholder(d.querySelector('.sk-ssheet__input'));
    return d;
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-mobile-search-button]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var d = ensureSearchSheet();
    if (!d.open) d.showModal();
    var q = d.querySelector('input[name="q"]');
    q.value = '';
    q.focus();
  }, true);

  function productSearch(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('form[action$="/search"]').forEach(function (form) {
      if (form.querySelector('[name="type"]')) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'type';
      input.value = 'product';
      form.appendChild(input);
    });
  }
  productSearch(document);
  document.addEventListener('shopify:section:load', function (e) { productSearch(e.target); });

  /* Search bars type their placeholder, "What are you looking for?", letter by letter, hold it,
     erase it and start again. They stop while the field is focused or filled; with reduced
     motion the phrase simply shows. The field keeps its aria-label for screen readers. */
  var TYPE_TEXT = 'What are you looking for?';
  var typeReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function typePlaceholder(input) {
    if (!input || input.__skTyping) return;
    input.__skTyping = true;
    if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', 'Search products, brands, categories');
    if (typeReduced) { input.placeholder = TYPE_TEXT; return; }
    var i = 0, dir = 1, timer = null;
    function idle() { return document.activeElement !== input && !input.value; }
    function tick() {
      if (!idle()) { input.placeholder = TYPE_TEXT; timer = setTimeout(tick, 600); return; }
      i += dir;
      input.placeholder = TYPE_TEXT.slice(0, i) + (dir > 0 && i < TYPE_TEXT.length ? '|' : '');
      var wait = dir > 0 ? 75 + Math.random() * 60 : 32;
      if (dir > 0 && i >= TYPE_TEXT.length) { dir = -1; wait = 2400; }
      else if (dir < 0 && i <= 0) { dir = 1; wait = 650; }
      timer = setTimeout(tick, wait);
    }
    input.placeholder = '';
    timer = setTimeout(tick, 700);
  }
  function typeAll(root) {
    (root || document).querySelectorAll('[data-live-search-input], .sk-ssheet__input').forEach(typePlaceholder);
  }
  typeAll(document);
  document.addEventListener('shopify:section:load', function (e) { typeAll(e.target); });
})();
